import {
  AccountTree,
  Assignment,
  AttachMoney,
  CalendarToday,
  Category,
  Cloud,
  Dashboard,
  ExpandLess, ExpandMore,
  Inventory,
  Logout,
  Payment,
  People,
  PersonAdd,
  ViewModule,
  WhatsApp
} from '@mui/icons-material';
import { Box, Collapse, Divider, IconButton, ListItemIcon, ListItemText, Tooltip, Typography } from '@mui/material';
import { Link, useLocation } from '@reach/router';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import apiClient from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import sphereLogo from '../images/sphere_logo.png';

const Nav = styled.nav`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 0 0 0 0;
  border-width: 0 1px 0 0;
  border-style: solid;
  border-color: rgba(255, 255, 255, 0.12);
  min-height: 100vh;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.18);
`;

const Logo = styled.img`
  height: 40px;
  width: auto;
  margin: 1rem auto;
`;

const Sidebar = styled.aside`
  position: sticky;        /* stays pinned on scroll */
  top: 0;
  height: 100dvh;          /* better than 100vh on mobile */
  display: flex;
  flex-direction: column;
  padding: 1rem;
  gap: 2rem;

  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-right: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.18);
`;

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  flex: 1;                /* takes remaining height */
  overflow-y: auto;       /* only the list scrolls */
  padding-right: 4px;     /* tiny room for scrollbar */
`;

const NavItem = styled.li`
  a {
    position: relative;    /* for the active bar */
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 12px;
    transition: all 0.2s ease;
    font-family: 'Inter', sans-serif;
    font-weight: 500;

    &:hover {
      background: rgba(255, 255, 255, 0.08);
      color: white;
    }

    &.active {
      background: rgba(255, 255, 255, 0.12);
      color: white;
    }
  }
`;


const Navigation = () => {
  const location = useLocation();
  const { logout } = useAuth();
  const [sections, setSections] = useState([]);
  const [openGroups, setOpenGroups] = useState({});
  const norm = (p) => (p.replace(/\/+$/, '') || '/');

  const isActivePath = (current, base) => {
    const cur = norm(current);
    const b = norm(base);
    if (b === '/') return cur === '/'; // чтобы Дашборд не подсвечивал всё
    return cur === b || cur.startsWith(b + '/');
  };

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const response = await apiClient.get('/sections');
        setSections(response.data);
      } catch (err) {
        console.error('Ошибка при загрузке разделов для навигации:', err);
      }
    };
    fetchSections();
  }, []);
  const searchParams = new URLSearchParams(location.search);
  const currentSectionId = searchParams.get('sectionId');

  const navGroups = [
    {
      title: 'Работа с клиентами',
      items: [
        { text: 'Лиды', icon: <PersonAdd />, path: '/leads', hint: 'Потенциальные клиенты, с которыми мы ещё работаем' },
        { text: 'Клиенты', icon: <People />, path: '/clients', hint: 'Список клиентов вашей организации' },
        { text: 'Сделки', icon: <AttachMoney />, path: '/deals', hint: 'Текущие продажи и их статус' },
        { text: 'Воронка', icon: <AccountTree />, path: '/pipeline', hint: 'Путь от лида до клиента по этапам' },
      ],
    },
    {
      title: 'Товары и услуги',
      items: [
        { text: 'Категории', icon: <Category />, path: '/categories', hint: 'Группы товаров и услуг' },
        { text: 'Товары', icon: <Inventory />, path: '/products', hint: 'Каталог товаров и услуг' },
      ],
    },
    {
      title: 'Организация работы',
      items: [
        { text: 'Календарь', icon: <CalendarToday />, path: '/calendar', hint: 'События, встречи и напоминания' },
        { text: 'Задачи', icon: <Assignment />, path: '/tasks', hint: 'Поручения для команды и себя' },
        { text: 'Разделы', icon: <ViewModule />, path: '/sections', hint: 'Пользовательские разделы/модули' },
      ],
    },
    {
      title: 'Финансы',
      items: [
        { text: 'Расходы', icon: <Payment />, path: '/expenses', hint: 'Учет расходов' },
      ],
    },
    {
      title: 'Коммуникации',
      items: [
        { text: 'WhatsApp', icon: <WhatsApp />, path: '/whatsapp', hint: 'Рассылки и сообщения в WhatsApp' },
        { text: 'Интеграции', icon: <ViewModule />, path: '/integrations', hint: 'Магазин сценариев и интеграций' },
      ],
    },
    {
      title: 'Файлы',
      items: [
        { text: 'Облако', icon: <Cloud />, path: '/cloud', hint: 'Хранилище файлов (Yandex Object Storage)' },
      ],
    },
    {
      title: 'Система',
      items: [
        { text: 'Дашборд', icon: <Dashboard />, path: '/', hint: 'Главная панель показателей' },
        { text: 'CMS', icon: <ViewModule />, path: '/cms', hint: 'Управление контентом: страницы и посты' },
        { text: 'Выйти', icon: <Logout />, path: '/auth', hint: 'Выход из системы', onClick: () => logout() },
      ],
    },
  ];

  // Инициализация состояния сворачивания групп из localStorage
  useEffect(() => {
    try {
      const saved = typeof window !== 'undefined' ? localStorage.getItem('nav_open_groups_v1') : null;
      const parsed = saved ? JSON.parse(saved) : {};
      const initial = {};
      navGroups.forEach(g => { initial[g.title] = parsed[g.title] !== undefined ? parsed[g.title] : true; });
      setOpenGroups(initial);
    } catch (e) {
      const initial = {};
      navGroups.forEach(g => { initial[g.title] = true; });
      setOpenGroups(initial);
    }
  }, []);

  const toggleGroup = (title) => {
    setOpenGroups(prev => {
      const next = { ...prev, [title]: !prev[title] };
      try {
        localStorage.setItem('nav_open_groups_v1', JSON.stringify(next));
      } catch (_) { }
      return next;
    });
  };

  return (
    <Sidebar>
      <Link to="/" style={{ textDecoration: 'none' }}>
        <Logo src={sphereLogo} alt="СФЕРА" />
      </Link>
      <NavList>
        {navGroups.map((group, gi) => (
          <Box key={group.title} sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1 }}>
              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                {group.title}
              </Typography>
              <IconButton size="small" onClick={() => toggleGroup(group.title)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                {openGroups[group.title] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
            </Box>

            <Collapse in={!!openGroups[group.title]} timeout="auto" unmountOnExit>
              {group.items.map((item) => {
                const isActive = isActivePath(location.pathname, item.path);
                const content = (
                  <Link
                    to={item.path}
                    onClick={item.onClick || undefined}
                    style={{
                      textDecoration: 'none',
                      color: 'rgba(255, 255, 255, 0.8)',
                      padding: '0.75rem 1rem',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      transition: 'all 0.2s ease',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: '500',
                    }}
                    className={isActive ? 'active' : ''}
                  >
                    <ListItemIcon sx={{
                      color: isActive ? 'white' : 'rgba(255, 255, 255, 0.7)',
                      minWidth: 40
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      sx={{
                        '& .MuiTypography-root': {
                          color: isActive ? 'white' : 'rgba(255, 255, 255, 0.7)',
                          fontWeight: isActive ? 'bold' : 'normal',
                          fontSize: '0.95rem'
                        }
                      }}
                    />
                    {isActive && (
                      <Box
                        sx={{
                          width: 4,
                          height: 35,
                          background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                          borderRadius: '4px',
                          position: 'absolute',
                          right: 0,
                          animation: 'slideIn 0.3s ease',
                          '@keyframes slideIn': {
                            from: { opacity: 0, transform: 'translateX(10px)' },
                            to: { opacity: 1, transform: 'translateX(0)' }
                          }
                        }}
                      />
                    )}
                  </Link>
                );
                return (
                  <NavItem key={item.text}>
                    {item.hint ? (
                      <Tooltip title={item.hint} placement="right" arrow>
                        {content}
                      </Tooltip>
                    ) : content}
                  </NavItem>
                );
              })}
            </Collapse>
            {gi < navGroups.length - 1 && (
              <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.08)' }} />
            )}
          </Box>
        ))}
        {sections.map((sec) => {
          const isActive = location.pathname === '/sections' && currentSectionId === sec.id;
          return (
            <NavItem key={sec.id}>
              <Link
                to={`/sections?sectionId=${sec.id}`}
                style={{
                  textDecoration: 'none',
                  color: 'rgba(255, 255, 255, 0.8)',
                  padding: '0.75rem 1rem',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'all 0.2s ease',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: '500',
                }}
                className={isActive ? 'active' : ''}
              >
                <ListItemIcon sx={{
                  color: isActive ? 'white' : 'rgba(255, 255, 255, 0.7)',
                  minWidth: 40
                }}>
                  <ViewModule />
                </ListItemIcon>
                <ListItemText
                  primary={sec.name}
                  sx={{
                    '& .MuiTypography-root': {
                      color: isActive ? 'white' : 'rgba(255, 255, 255, 0.7)',
                      fontWeight: isActive ? 'bold' : 'normal',
                      fontSize: '0.95rem'
                    }
                  }}
                />
                {isActive && (
                  <Box
                    sx={{
                      width: 4,
                      height: 35,
                      background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                      borderRadius: '4px',
                      position: 'absolute',
                      right: 0,
                      animation: 'slideIn 0.3s ease',
                      '@keyframes slideIn': {
                        from: { opacity: 0, transform: 'translateX(10px)' },
                        to: { opacity: 1, transform: 'translateX(0)' }
                      }
                    }}
                  />
                )}
              </Link>
            </NavItem>
          );
        })}
      </NavList>

      {/* Кнопка выхода теперь в группе "Система" */}
    </Sidebar>
  );
};

export default Navigation; 